import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchWithRetry } from '@/lib/apiRequest';

export type PublicationMonitorType = 'process' | 'lawyer_oab' | 'lawyer_name' | 'mixed';

export interface PublicationMonitor {
  id: string;
  workspaceId: string;
  name: string;
  monitorType: PublicationMonitorType;
  processNumber: string | null;
  lawyerOab: string | null;
  lawyerUf: string | null;
  lawyerName: string | null;
  tribunal: string | null;
  caderno: string | null;
  defaultStartDate: string | null;
  defaultEndDate: string | null;
  lookbackDays: number | null;
  isActive: boolean;
  lastRunAt: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PublicationImportRun {
  id: string;
  workspaceId: string;
  monitorId: string | null;
  source: string;
  status: 'running' | 'success' | 'partial_error' | 'failed' | string;
  startedAt: string;
  finishedAt: string | null;
  totalFetched: number;
  totalNormalized: number;
  totalInserted: number;
  totalSkippedAsDuplicate: number;
  errors: string[];
  request: Record<string, unknown> | null;
  createdAt: string;
}

export interface PublicationCronStatus {
  activeMonitorCount: number;
  lastAutomaticRun: PublicationImportRun | null;
  lastAutomaticRunAt: string | null;
  lastAutomaticStatus: string | null;
  lastAutomaticError: string | null;
}

export interface CreatePublicationMonitorInput {
  name: string;
  monitorType?: PublicationMonitorType | string | null;
  processNumber?: string | null;
  lawyerOab?: string | null;
  lawyerUf?: string | null;
  lawyerName?: string | null;
  tribunal?: string | null;
  caderno?: string | null;
  defaultStartDate?: string | null;
  defaultEndDate?: string | null;
  lookbackDays?: number | string | null;
  isActive?: boolean | number | string | null;
}

export type UpdatePublicationMonitorInput = Partial<CreatePublicationMonitorInput>;

type UsePublicationMonitorsOptions = {
  autoLoad?: boolean;
};

type ApiResponse = {
  success?: boolean;
  monitors?: unknown;
  monitor?: unknown;
  runs?: unknown;
  status?: unknown;
  error?: string;
  totalFetched?: number;
  totalNormalized?: number;
  totalInserted?: number;
  totalSkippedAsDuplicate?: number;
  errors?: string[];
  runId?: string;
};

const toNullableText = (value: unknown) => {
  if (value === undefined || value === null) {
    return null;
  }

  const text = String(value).trim();
  return text === '' ? null : text;
};

const toNullableNumber = (value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const number = Number(String(value).replace(',', '.'));
  return Number.isFinite(number) ? number : null;
};

const toBoolean = (value: unknown, fallback = true) => {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  const normalized = String(value).trim().toLowerCase();
  if (['0', 'false', 'inactive', 'inativo', 'archived', 'arquivado', 'nao', 'não'].includes(normalized)) {
    return false;
  }

  return true;
};

const normalizeMonitorType = (value: unknown): PublicationMonitorType => {
  const normalized = String(value ?? 'mixed').trim().toLowerCase();
  if (normalized === 'process' || normalized === 'lawyer_oab' || normalized === 'lawyer_name' || normalized === 'mixed') {
    return normalized;
  }

  return 'mixed';
};

const normalizeMonitor = (value: any): PublicationMonitor => ({
  id: String(value?.id ?? ''),
  workspaceId: String(value?.workspaceId ?? value?.workspace_id ?? 'default'),
  name: String(value?.name ?? '').trim(),
  monitorType: normalizeMonitorType(value?.monitorType ?? value?.monitor_type),
  processNumber: toNullableText(value?.processNumber ?? value?.process_number),
  lawyerOab: toNullableText(value?.lawyerOab ?? value?.lawyer_oab),
  lawyerUf: toNullableText(value?.lawyerUf ?? value?.lawyer_uf),
  lawyerName: toNullableText(value?.lawyerName ?? value?.lawyer_name),
  tribunal: toNullableText(value?.tribunal),
  caderno: toNullableText(value?.caderno),
  defaultStartDate: toNullableText(value?.defaultStartDate ?? value?.default_start_date),
  defaultEndDate: toNullableText(value?.defaultEndDate ?? value?.default_end_date),
  lookbackDays: toNullableNumber(value?.lookbackDays ?? value?.lookback_days),
  isActive: toBoolean(value?.isActive ?? value?.is_active, true),
  lastRunAt: toNullableText(value?.lastRunAt ?? value?.last_run_at),
  lastSuccessAt: toNullableText(value?.lastSuccessAt ?? value?.last_success_at),
  lastError: toNullableText(value?.lastError ?? value?.last_error),
  createdAt: toNullableText(value?.createdAt ?? value?.created_at) ?? new Date().toISOString(),
  updatedAt: toNullableText(value?.updatedAt ?? value?.updated_at) ?? new Date().toISOString(),
});

const normalizeRun = (value: any): PublicationImportRun => ({
  id: String(value?.id ?? ''),
  workspaceId: String(value?.workspaceId ?? value?.workspace_id ?? 'default'),
  monitorId: toNullableText(value?.monitorId ?? value?.monitor_id),
  source: String(value?.source ?? 'djen'),
  status: String(value?.status ?? 'running'),
  startedAt: toNullableText(value?.startedAt ?? value?.started_at) ?? new Date().toISOString(),
  finishedAt: toNullableText(value?.finishedAt ?? value?.finished_at),
  totalFetched: toNullableNumber(value?.totalFetched ?? value?.total_fetched) ?? 0,
  totalNormalized: toNullableNumber(value?.totalNormalized ?? value?.total_normalized) ?? 0,
  totalInserted: toNullableNumber(value?.totalInserted ?? value?.total_inserted) ?? 0,
  totalSkippedAsDuplicate: toNullableNumber(value?.totalSkippedAsDuplicate ?? value?.total_skipped_as_duplicate) ?? 0,
  errors: Array.isArray(value?.errors) ? value.errors.map((item: unknown) => String(item)) : [],
  request: value?.request ?? null,
  createdAt: toNullableText(value?.createdAt ?? value?.created_at) ?? new Date().toISOString(),
});

const normalizeCronStatus = (value: any): PublicationCronStatus => ({
  activeMonitorCount: Number(value?.activeMonitorCount ?? value?.active_monitor_count ?? 0) || 0,
  lastAutomaticRun: value?.lastAutomaticRun ? normalizeRun(value.lastAutomaticRun) : null,
  lastAutomaticRunAt: toNullableText(value?.lastAutomaticRunAt ?? value?.last_automatic_run_at),
  lastAutomaticStatus: toNullableText(value?.lastAutomaticStatus ?? value?.last_automatic_status),
  lastAutomaticError: toNullableText(value?.lastAutomaticError ?? value?.last_automatic_error),
});

export function usePublicationMonitors(options: UsePublicationMonitorsOptions = {}) {
  const { autoLoad = true } = options;
  const [monitors, setMonitors] = useState<PublicationMonitor[]>([]);
  const [runs, setRuns] = useState<PublicationImportRun[]>([]);
  const [isLoadingMonitors, setIsLoadingMonitors] = useState(true);
  const [isLoadingRuns, setIsLoadingRuns] = useState(false);
  const [isSavingMonitor, setIsSavingMonitor] = useState(false);
  const [monitorError, setMonitorError] = useState<string | null>(null);
  const [monitorSuccess, setMonitorSuccess] = useState<string | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [runSuccess, setRunSuccess] = useState<string | null>(null);
  const [cronStatus, setCronStatus] = useState<PublicationCronStatus | null>(null);
  const [isLoadingCronStatus, setIsLoadingCronStatus] = useState(false);
  const requestIdRef = useRef(0);

  const loadMonitors = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setIsLoadingMonitors(true);
    setMonitorError(null);

    try {
      const response = await fetchWithRetry('/api/publications/monitors');
      const data = (await response.json()) as ApiResponse;

      if (!response.ok || !data.success || !Array.isArray(data.monitors)) {
        throw new Error(data.error || `GET /api/publications/monitors falhou com status ${response.status}`);
      }

      if (requestId === requestIdRef.current) {
        setMonitors(data.monitors.map(normalizeMonitor));
      }
    } catch (error: any) {
      console.error('Erro ao carregar monitores DJEN/CNJ.', error);
      if (requestId === requestIdRef.current) {
        setMonitors([]);
        setMonitorError(error?.message || 'Não foi possível carregar os monitores DJEN/CNJ.');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoadingMonitors(false);
      }
    }
  }, []);

  const loadRuns = useCallback(async (monitorId?: string | null) => {
    setIsLoadingRuns(true);
    setRunError(null);

    try {
      const url = monitorId ? `/api/publications/monitors/${encodeURIComponent(monitorId)}/runs` : '/api/publications/import-runs';
      const response = await fetchWithRetry(url);
      const data = (await response.json()) as ApiResponse;

      if (!response.ok || !data.success || !Array.isArray(data.runs)) {
        throw new Error(data.error || `GET ${url} falhou com status ${response.status}`);
      }

      setRuns(data.runs.map(normalizeRun));
    } catch (error: any) {
      console.error('Erro ao carregar execuções DJEN/CNJ.', error);
      setRuns([]);
      setRunError(error?.message || 'Não foi possível carregar o histórico de execuções.');
    } finally {
      setIsLoadingRuns(false);
    }
  }, []);

  const loadCronStatus = useCallback(async () => {
    setIsLoadingCronStatus(true);

    try {
      const response = await fetchWithRetry('/api/publications/cron/status');
      const data = (await response.json()) as ApiResponse;

      if (!response.ok || !data.success || !data.status) {
        throw new Error(data.error || `GET /api/publications/cron/status falhou com status ${response.status}`);
      }

      setCronStatus(normalizeCronStatus(data.status));
    } catch (error: any) {
      console.error('Erro ao carregar status do cron DJEN/CNJ.', error);
      setCronStatus(null);
    } finally {
      setIsLoadingCronStatus(false);
    }
  }, []);

  const createMonitor = useCallback(async (input: CreatePublicationMonitorInput) => {
    setIsSavingMonitor(true);
    setMonitorError(null);
    setMonitorSuccess(null);

    try {
      const response = await fetchWithRetry('/api/publications/monitors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      const data = (await response.json()) as ApiResponse;
      if (!response.ok || !data.success || !data.monitor) {
        throw new Error(data.error || `POST /api/publications/monitors falhou com status ${response.status}`);
      }

      const created = normalizeMonitor(data.monitor);
      setMonitors((prev) => [created, ...prev.filter((item) => item.id !== created.id)]);
      setMonitorSuccess('Monitor cadastrado com sucesso.');
      return created;
    } catch (error: any) {
      setMonitorError(error?.message || 'Não foi possível cadastrar o monitor.');
      throw error;
    } finally {
      setIsSavingMonitor(false);
    }
  }, []);

  const updateMonitor = useCallback(async (id: string, input: UpdatePublicationMonitorInput) => {
    setIsSavingMonitor(true);
    setMonitorError(null);
    setMonitorSuccess(null);

    try {
      const response = await fetchWithRetry(`/api/publications/monitors/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      const data = (await response.json()) as ApiResponse;
      if (!response.ok || !data.success || !data.monitor) {
        throw new Error(data.error || `PUT /api/publications/monitors/${id} falhou com status ${response.status}`);
      }

      const updated = normalizeMonitor(data.monitor);
      setMonitors((prev) => [updated, ...prev.filter((item) => item.id !== updated.id)]);
      setMonitorSuccess('Monitor atualizado com sucesso.');
      return updated;
    } catch (error: any) {
      setMonitorError(error?.message || 'Não foi possível atualizar o monitor.');
      throw error;
    } finally {
      setIsSavingMonitor(false);
    }
  }, []);

  const deleteMonitor = useCallback(async (id: string) => {
    setIsSavingMonitor(true);
    setMonitorError(null);
    setMonitorSuccess(null);

    try {
      const response = await fetchWithRetry(`/api/publications/monitors/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });

      const data = (await response.json()) as ApiResponse;
      if (!response.ok || !data.success) {
        throw new Error(data.error || `DELETE /api/publications/monitors/${id} falhou com status ${response.status}`);
      }

      setMonitors((prev) => prev.filter((item) => item.id !== id));
      setMonitorSuccess('Monitor removido com sucesso.');
    } catch (error: any) {
      setMonitorError(error?.message || 'Não foi possível remover o monitor.');
      throw error;
    } finally {
      setIsSavingMonitor(false);
    }
  }, []);

  const runMonitor = useCallback(async (id: string) => {
    setIsSavingMonitor(true);
    setRunError(null);
    setRunSuccess(null);

    try {
      const response = await fetchWithRetry(
        `/api/publications/monitors/${encodeURIComponent(id)}/run`,
        {
          method: 'POST',
        },
        {
          timeoutMs: 120000,
          retries: 0,
        }
      );

      const data = (await response.json()) as ApiResponse;

      if (!response.ok || !data.success) {
        throw new Error(data.error || `POST /api/publications/monitors/${id}/run falhou com status ${response.status}`);
      }

      const summary = {
        runId: data.runId ?? '',
        totalFetched: data.totalFetched ?? 0,
        totalNormalized: data.totalNormalized ?? 0,
        totalInserted: data.totalInserted ?? 0,
        totalSkippedAsDuplicate: data.totalSkippedAsDuplicate ?? 0,
        errors: data.errors ?? [],
      };

      setRunSuccess(
        summary.totalInserted > 0
          ? `Importação concluída: ${summary.totalInserted} nova(s) publicação(ões).`
          : summary.totalFetched > 0
            ? `Consulta concluída: ${summary.totalFetched} publicação(ões) encontrada(s), todas já existentes.`
            : 'Nenhuma publicação encontrada no período consultado.'
      );

      await loadMonitors();
      await loadRuns(id);
      await loadCronStatus();

      return summary;
    } catch (error: any) {
      setRunError(error?.message || 'Não foi possível executar o monitor.');
      throw error;
    } finally {
      setIsSavingMonitor(false);
    }
  }, [loadCronStatus, loadMonitors, loadRuns]);

  const clearPublicationMonitorFeedback = useCallback(() => {
    setMonitorError(null);
    setMonitorSuccess(null);
    setRunError(null);
    setRunSuccess(null);
  }, []);

  useEffect(() => {
    if (!autoLoad) {
      setIsLoadingMonitors(false);
      return;
    }

    void loadMonitors();
    void loadRuns();
    void loadCronStatus();
  }, [autoLoad, loadCronStatus, loadMonitors, loadRuns]);

  return {
    monitors,
    runs,
    cronStatus,
    isLoadingMonitors,
    isLoadingRuns,
    isLoadingCronStatus,
    isSavingMonitor,
    monitorError,
    monitorSuccess,
    runError,
    runSuccess,
    loadMonitors,
    loadRuns,
    loadCronStatus,
    createMonitor,
    updateMonitor,
    deleteMonitor,
    runMonitor,
    clearPublicationMonitorFeedback,
  };
}
